import { supabase } from './supabase';

export interface StockItem {
  id: string;
  business_id: string;
  name: string;
  description: string | null;
  category_id: string | null;
  supplier_id: string | null;
  unit: string;
  sku: string | null;
  price: number;
  is_sellable: boolean;
  acquisition_cost: number;
  tax_rate: number;
  /** When true this product is never taxed on invoices or booking totals. */
  tax_exempt?: boolean;
  quantity: number;
  reorder_point: number | null;
  max_quantity: number | null;
  /** When false, the item is sellable without stock levels (made-to-order):
   *  ledger rows are recorded as a movement log but quantity never changes. */
  track_inventory: boolean;
  /** When true, tracked items keep selling past zero (quantity may go
   *  negative — a backorder count). */
  allow_oversell?: boolean;
  show_online?: boolean;
  /** Refundable per-unit container deposit in cents (e.g. 1000 = $10/jug).
   *  Charged on delivery, refunded on return; never taxed. 0 = no deposit. */
  deposit_cents?: number;
  /** Word in front of "deposit". Blank displays as "Deposit"; "bottle" as "Bottle deposit". */
  deposit_label?: string | null;
  /** When true, containers customers hand back (swap empties, pickups) go
   *  straight back into stock — empties are treated as instantly sellable.
   *  Lost containers never restock. Ignored for rental items. */
  restock_returns?: boolean;
  /** Rented on a recurring agreement instead of sold. The rate locks per
   *  customer at signup; billing runs automatically until canceled. */
  rental_enabled?: boolean;
  rental_price_cents?: number;
  rental_interval?: 'month' | 'week';
  /** Checkout agreement for this rental. Blank uses the standard agreement. */
  rental_agreement?: string | null;
  created_at: string;
  updated_at: string;
  is_deleted?: boolean;
  is_one_time?: boolean;
  // Variant + Shopify sync fields (Phase 1 schema)
  product_group_id?: string | null;
  option_values?: Record<string, string>;
  shopify_variant_id?: string | null;
  shopify_inventory_item_id?: string | null;
  barcode?: string | null;
  compare_at_price?: number | null;
  weight?: number | null;
  weight_unit?: string | null;
  image_url?: string | null;
  product_group_image_id?: string | null;
  sync_source?: string | null;
  last_sync_hash?: string | null;
  synced_from_shopify_at?: string | null;
  category?: {
    id: string;
    name: string;
  };
}

interface StockTransaction {
  id: string;
  item_id: string;
  type: 'purchase' | 'sale' | 'adjustment';
  quantity: number;
  price: number;
  notes: string | null;
  created_at: string;
  created_by: string | null;
  product_sale_id?: string | null;
  booking_id?: string | null;
}

export interface StockHistoryEntry {
  id: string;
  item_id: string;
  type: 'purchase' | 'sale' | 'adjustment';
  quantity: number;
  price: number;
  notes: string | null;
  created_at: string;
  created_by: string | null;
  item_name: string;
  item_description: string | null;
  item_unit: string;
  category_name: string | null;
  supplier_name: string | null;
  created_by_email: string | null;
  created_by_name?: string | null;
  transaction_type: string;
  quantity_change: number;
  is_item_deleted?: boolean;
}

export interface TransactionHistoryEntry {
  id: string;
  transaction_type: 'Stock Transaction' | 'Product Sale' | 'Grouped Sale';
  type: string; // 'Received', 'Withdrawn', 'Adjusted', 'Sale'
  created_at: string;
  created_by_email: string | null;
  
  // Stock transaction fields
  item_id?: string;
  item_name?: string;
  item_unit?: string;
  quantity_change?: number;
  category_name?: string | null;
  notes?: string | null;
  is_item_deleted?: boolean;
  product_sale_id?: string | null;
  
  // Product sale fields
  sale_number?: string;
  customer_name?: string | null;
  customer_email?: string | null;
  total_amount?: number;
  payment_status?: string;
  item_count?: number;
  total_items_sold?: number;
  
  // Grouped transaction fields
  related_transactions?: TransactionHistoryEntry[];
  is_grouped?: boolean;
}

export async function getStockItems(businessId: string, options?: { includePendingOneTimeItems?: boolean; onOrderQuantities?: Record<string, number> }) {
  const { data, error } = await supabase
    .from('stock_items')
    .select(`
      *,
      category:stock_categories(
        id,
        name
      ),
      stock_history:stock_transactions(
        id,
        type,
        quantity,
        price,
        created_at
      )
    `)
    .eq('business_id', businessId)
    .eq('is_deleted', false) // Exclude deleted items
    .order('name');

  if (error) throw error;
  
  // Filter out one-time items with 0 quantity at the application level
  // (PostgREST doesn't easily support complex OR conditions with AND)
  // Exception: Include pending one-time items (those with supplier_id) if requested
  // OR if they have items on order
  console.log('🔍 Filtering stock items. Options:', { 
    includePendingOneTimeItems: options?.includePendingOneTimeItems,
    onOrderQuantitiesKeys: options?.onOrderQuantities ? Object.keys(options.onOrderQuantities) : [],
    onOrderQuantities: options?.onOrderQuantities
  });
  
  const filteredData = (data || []).filter(item => {
    // Exclude one-time items that have reached 0 quantity
    // UNLESS:
    // 1. They're pending (have supplier_id) and we're including pending items
    // 2. They have items on order (quantity > 0 in onOrderQuantities)
    if (item.is_one_time && item.quantity === 0) {
      // Include if it has a supplier (pending order) and we're including pending items
      if (options?.includePendingOneTimeItems && item.supplier_id) {
        console.log(`✅ Including one-time item "${item.name}" (id: ${item.id}) - has supplier_id`);
        return true;
      }
      // Include if it has items on order
      const onOrderQty = options?.onOrderQuantities?.[item.id] || 0;
      console.log(`🔍 Checking one-time item "${item.name}" (id: ${item.id}) - quantity: 0, onOrderQty: ${onOrderQty}, onOrderQuantities has key: ${options?.onOrderQuantities ? (item.id in options.onOrderQuantities) : false}`);
      if (onOrderQty > 0) {
        console.log(`✅ Including one-time item "${item.name}" (id: ${item.id}) with ${onOrderQty} units on order`);
        return true;
      }
      console.log(`❌ Excluding one-time item "${item.name}" (id: ${item.id}) - quantity: 0, onOrder: ${onOrderQty}`);
      return false;
    }
    return true;
  });
  
  return filteredData as (StockItem & { stock_history: StockTransaction[] })[];
}

async function getStockItem(id: string) {
  const { data, error } = await supabase
    .from('stock_items')
    .select(`
      *,
      category:stock_categories(
        id,
        name
      )
    `)
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as StockItem;
}

export async function createStockItem(item: Omit<StockItem, 'id' | 'created_at' | 'updated_at'>) {
  // First create the stock item with zero quantity
  // For one-time items, reorder_point and max_quantity should be null
  const isOneTime = item.is_one_time || false;
  const tracksInventory = item.track_inventory !== false;
  const insertData: any = {
    ...item,
    quantity: 0, // Always start with 0 quantity
    is_one_time: isOneTime,
    track_inventory: tracksInventory
  };
  
  if (isOneTime || !tracksInventory) {
    // One-time and untracked (made-to-order) items have no stock thresholds.
    insertData.reorder_point = null;
    insertData.max_quantity = null;
  } else {
    insertData.reorder_point = item.reorder_point ?? 0;
    insertData.max_quantity = item.max_quantity ?? 100;
  }
  
  const { data: stockItem, error: stockError } = await supabase
    .from('stock_items')
    .insert([insertData])
    .select()
    .single();

    if (stockError) {
      console.error('Error creating stock item:', stockError);
      throw new Error(stockError.message || 'Failed to create stock item');
    }

  // If initial quantity is specified, create a purchase transaction
  // (untracked items have no stock level — nothing to receive)
  if (tracksInventory && item.quantity > 0) {
    try {
      await createStockTransaction({
        item_id: stockItem.id,
        type: 'purchase',
        quantity: item.quantity,
        price: item.acquisition_cost,
        notes: 'Initial stock'
      });
    } catch (err) {
      // If transaction fails, delete the item
      await supabase
        .from('stock_items')
        .delete()
        .eq('id', stockItem.id);
      throw err;
    }
  }

  // Get the final item state with all relations
  const { data: createdItem, error: fetchError } = await supabase
    .from('stock_items')
    .select(`
      *,
      category:stock_categories(
        id,
        name
      )
    `)
    .eq('id', stockItem.id)
    .single();

  if (fetchError) throw fetchError;
  return createdItem as StockItem;
}

export async function updateStockItem(id: string, updates: Partial<StockItem>) {
  const { data, error } = await supabase
    .from('stock_items')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as StockItem;
}

export async function deleteStockItem(id: string) {
  const { error } = await supabase
    .from('stock_items')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function createStockTransaction(transaction: Omit<StockTransaction, 'id' | 'created_at' | 'created_by'>) {
  // Validate transaction
  if (!transaction.item_id) {
    throw new Error('Item ID is required');
  }
  if (!transaction.type) {
    throw new Error('Transaction type is required');
  }
  if (!transaction.quantity || transaction.quantity <= 0) {
    throw new Error('Quantity must be greater than 0');
  }
  if (typeof transaction.price !== 'number' || transaction.price < 0) {
    throw new Error('Price cannot be negative');
  }

  try {
    // Create transaction record
    const { data: transactionData, error: transactionError } = await supabase
      .from('stock_transactions')
      .insert([{
        item_id: transaction.item_id,
        type: transaction.type,
        quantity: transaction.quantity,
        price: transaction.price,
        notes: transaction.notes || null,
        booking_id: transaction.booking_id || null
      }])
      .select()
      .single();

    if (transactionError) throw transactionError;
    return transactionData as StockTransaction;

  } catch (err) {
    console.error('Error processing stock transaction:', err);
    throw err instanceof Error ? err : new Error('Failed to process stock transaction');
  }
}

export async function cleanupBookingProducts(bookingId: string) {
  if (!bookingId) {
    throw new Error('Booking ID is required');
  }

  try {
    console.log(`🧹 Cleaning up products for booking: ${bookingId}`);

    // Remove all product_booking_sales entries for this booking
    const { error: productSalesError } = await supabase
      .from('product_booking_sales')
      .delete()
      .eq('booking_id', bookingId);

    if (productSalesError) {
      console.error('Error removing product_booking_sales:', productSalesError);
      throw productSalesError;
    }

    // Remove all stock_transactions entries for this booking
    const { error: stockTransactionsError } = await supabase
      .from('stock_transactions')
      .delete()
      .eq('booking_id', bookingId);

    if (stockTransactionsError) {
      console.error('Error removing stock_transactions:', stockTransactionsError);
      throw stockTransactionsError;
    }

    console.log(`✅ Successfully cleaned up products for booking: ${bookingId}`);
    return { success: true };

  } catch (error) {
    console.error('Error cleaning up booking products:', error);
    throw error;
  }
}

async function getStockTransactions(itemId: string) {
  const { data, error } = await supabase
    .from('stock_transactions')
    .select('*')
    .eq('item_id', itemId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as StockTransaction[];
}

export async function getStockHistory(businessId: string) {
  const { data, error } = await supabase
    .from('stock_history_with_details') 
    .select(`
      *,
      item:stock_items(
        category:stock_categories(
          id,
          name
        )
      )
    `)
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as StockHistoryEntry[];
}

export async function getTransactionHistory(businessId: string): Promise<TransactionHistoryEntry[]> {
  try {
    // Get stock transactions directly from the view
    const { data: stockTransactions, error: stockError } = await supabase
      .from('stock_history_with_details')
      .select('*')
      .eq('business_id', businessId);

    if (stockError) throw stockError;

    // Get product sales to match with transactions that have product_sale_id
    const { data: productSales, error: salesError } = await supabase
      .from('product_sales')
      .select(`
        id, 
        sale_number, 
        total_amount,
        payment_status,
        payment_method,
        customer_id,
        customer:customers!product_sales_customer_id_fkey(first_name, last_name)
      `)
      .eq('business_id', businessId);

    if (salesError) throw salesError;

    // Create maps for quick lookup
    const saleNumberMap = new Map();
    const saleAmountMap = new Map();
    const customerNameMap = new Map();
    const paymentStatusMap = new Map();
    const paymentMethodMap = new Map();
    (productSales || []).forEach(sale => {
      saleNumberMap.set(sale.id, sale.sale_number);
      saleAmountMap.set(sale.id, sale.total_amount);
      paymentStatusMap.set(sale.id, sale.payment_status);
      paymentMethodMap.set(sale.id, sale.payment_method);
      
      // Build customer name if customer exists
      if (sale.customer && sale.customer.first_name && sale.customer.last_name) {
        const customerName = `${sale.customer.first_name} ${sale.customer.last_name}`.trim();
        customerNameMap.set(sale.id, customerName);
      }
    });

    // Convert stock transactions to unified format
    const stockEntries: TransactionHistoryEntry[] = (stockTransactions || []).map(transaction => ({
      id: transaction.id,
      transaction_type: 'Stock Transaction' as const,
      type: transaction.transaction_type, // 'Received', 'Withdrawn', 'Adjusted'
      created_at: transaction.created_at,
      created_by_email: transaction.created_by_email,
      
      // Stock transaction specific fields
      item_id: transaction.item_id,
      item_name: transaction.item_name,
      item_unit: transaction.item_unit,
      quantity_change: transaction.quantity_change,
      category_name: transaction.category_name,
      notes: transaction.notes,
      is_item_deleted: transaction.is_item_deleted,
      product_sale_id: transaction.product_sale_id,
      
      // Add sale info if this transaction is linked to a product sale
      sale_number: transaction.product_sale_id ? saleNumberMap.get(transaction.product_sale_id) : null,
      total_amount: transaction.product_sale_id ? saleAmountMap.get(transaction.product_sale_id) : null,
      customer_name: transaction.product_sale_id ? customerNameMap.get(transaction.product_sale_id) : null,
      payment_status: transaction.product_sale_id ? paymentStatusMap.get(transaction.product_sale_id) : null,
      payment_method: transaction.product_sale_id ? paymentMethodMap.get(transaction.product_sale_id) : null
    }));

    // Sort by created_at (newest first)
    stockEntries.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    return stockEntries;
  } catch (error) {
    console.error('Error fetching transaction history:', error);
    throw error;
  }
}

export async function deleteStockHistoryEntry(id: string) {
  const { error: deleteError } = await supabase
    .from('stock_transactions')
    .delete()
    .eq('id', id);

  if (deleteError) throw deleteError;
}

// Delete a grouped transaction (both stock transaction and product sale)
export async function deleteGroupedTransaction(stockTransactionId: string, productSaleId: string) {
  try {
    // Delete the stock transaction first
    const { error: stockError } = await supabase
      .from('stock_transactions')
      .delete()
      .eq('id', stockTransactionId);

    if (stockError) throw stockError;

    // Delete any associated payment records (for invoices with Stripe payment links)
    const { error: paymentError } = await supabase
      .from('payments')
      .delete()
      .eq('product_sale_id', productSaleId);

    if (paymentError) {
      console.warn('Warning: Could not delete payment records:', paymentError);
      // Don't throw error for payment deletion failure - continue with product sale deletion
    }

    // Delete the product sale
    const { error: saleError } = await supabase
      .from('product_sales')
      .delete()
      .eq('id', productSaleId);

    if (saleError) throw saleError;
  } catch (error) {
    console.error('Error deleting grouped transaction:', error);
    throw error;
  }
}

async function getStockStats(businessId: string) {
  const { data: items, error } = await supabase
    .from('stock_items')
    .select('*')
    .eq('business_id', businessId);

  if (error) throw error;

  const stats = {
    totalItems: items.length,
    lowStock: items.filter(item => item.quantity <= item.reorder_point).length,
    totalValue: items.reduce((sum, item) => sum + (item.track_inventory === false ? 0 : item.quantity * item.acquisition_cost), 0),
    averageValue: items.length > 0 
      ? items.reduce((sum, item) => sum + (item.quantity * item.acquisition_cost), 0) / items.length 
      : 0
  };

  return stats;
}